import { forwardRef } from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical } from "@fortawesome/free-solid-svg-icons";
import {
  jobAccentColor,
  nextActionFor,
  instructionCount,
  shortId,
  jobTitle,
} from "./jobStatus";

// A real button, so the menu keeps working inside a dnd Draggable.
const KebabToggle = forwardRef(({ onClick }, ref) => (
  <button
    ref={ref}
    type="button"
    className="btn btn-sm text-muted p-0 px-2"
    aria-label="Job actions"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }}
  >
    <FontAwesomeIcon icon={faEllipsisVertical} />
  </button>
));

/**
 * One job card on the board. Read-only apart from delete: it shows what the
 * machine reported and links out — it never edits the job.
 */
const JobItem = ({ job, onOpenDetails, onTeleprompter, onDelete }) => {
  const accent = jobAccentColor(job);
  const nextAction = nextActionFor(job);
  const title = jobTitle(job);
  const instr = instructionCount(job);
  const scripts = job?.scripts?.length ?? 0;

  // A job without a post is normal — the machine only links one once it scripts.
  const hasPost = Boolean(job?.post_id);

  const stop = (e) => e.stopPropagation();

  return (
    <div
      className="card mb-2"
      style={{ borderLeft: `3px solid ${accent}`, cursor: "pointer" }}
      onClick={() => onOpenDetails(job)}
    >
      <div className="card-body p-3">
        <div className="d-flex align-items-start">
          <h5
            className="font-size-14 mb-2 flex-grow-1"
            style={{
              fontWeight: title.muted ? 500 : 600,
              fontStyle: title.muted ? "italic" : "normal",
              color: title.muted ? "#74788d" : undefined,
              lineHeight: 1.35,
            }}
          >
            {title.text}
          </h5>
          <div onClick={stop} onMouseDown={stop} onPointerDown={stop}>
            <Dropdown align="end">
              <Dropdown.Toggle as={KebabToggle} />
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => onOpenDetails(job)}>
                  Details
                </Dropdown.Item>
                <Dropdown.Item
                  disabled={!hasPost}
                  onClick={() => hasPost && onTeleprompter(job)}
                >
                  Teleprompter
                </Dropdown.Item>
                {job?.link && (
                  <Dropdown.Item
                    href={job.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open source link
                  </Dropdown.Item>
                )}
                <Dropdown.Divider />
                <Dropdown.Item
                  className="text-danger"
                  onClick={() => onDelete(job)}
                >
                  Delete job
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div className="mb-2">
          {job?.link_type && (
            <span className="badge me-1 font-size-12 rounded-pill badge-info-subtle">
              {job.link_type}
            </span>
          )}
          {job?.type && (
            <span className="badge me-1 font-size-12 rounded-pill badge-warning-subtle">
              {job.type}
            </span>
          )}
          {job?.brand && (
            <span className="badge me-1 font-size-12 rounded-pill badge-secondary-subtle">
              {job.brand}
            </span>
          )}
        </div>

        <div
          className="d-flex justify-content-between text-muted border-top pt-2"
          style={{ fontSize: "11px", fontFamily: "monospace" }}
        >
          <span>
            {instr} instr · {scripts} scripts
          </span>
          <span>{shortId(job)}</span>
        </div>

        <div
          className="mt-2"
          style={{
            fontSize: "12px",
            fontWeight: 500,
            color: nextAction.warn ? "#f46a6a" : "#343a40",
          }}
        >
          {nextAction.label}
        </div>
      </div>
    </div>
  );
};

export default JobItem;
